import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Modal, Button, Tag } from "@douyinfe/semi-ui";
import { IconMusic, IconPlay, IconPause } from "@douyinfe/semi-icons";
import BenzAMRRecorder from "amr-audio-recorder";

/** 取 URL/文件名后缀（去掉查询串） */
function extOf(s: string): string {
  if (!s) return "";
  return s.split("?")[0].split(".").pop()?.toLowerCase() || "";
}

/** 判断是否为 AMR/AMR-WB 格式：优先看 URL，再看原始文件名兜底 */
function isAmrFormat(...sources: string[]): boolean {
  return sources.some((s) => {
    const ext = extOf(s);
    return ext === "amr" || ext === "awb";
  });
}

/** 判断是否为 AMR-WB (AWB) 格式 */
function isAmrWbFormat(url: string): boolean {
  return extOf(url) === "awb";
}

/** 将秒数格式化为 mm:ss */
function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

interface AudioPlayerModalProps {
  visible: boolean;
  url: string;
  title?: string;
  /** 原始录音文件名（recordName），URL 无后缀时用于 AMR 格式兜底识别 */
  fileName?: string;
  /** 转写全文。传入时展示"播放器 + 对话"联动视图：点气泡跳转、播放跟随高亮 */
  transcript?: string;
  onClose: () => void;
}

interface DialogueLine {
  time: string;
  sec: number;
  speaker: string;
  content: string;
}

/**
 * 解析 transcript_text 为结构化数据。
 *
 * 格式约定（TingwuClient 生成）：
 *   【摘要】标题\n摘要正文\n\n【对话】\n[MM:SS] 说话人：内容\n...
 */
function parseTranscript(text: string) {
  if (!text) return { summary: null as string | null, lines: [] as DialogueLine[] };

  const summaryIdx = text.indexOf("【摘要】");
  const dialogueIdx = text.indexOf("【对话】");

  let summary: string | null = null;
  let dialoguePart = text;

  if (summaryIdx >= 0 && dialogueIdx >= 0 && summaryIdx < dialogueIdx) {
    summary = text.substring(summaryIdx + 4, dialogueIdx).trim();
    dialoguePart = text.substring(dialogueIdx + 4);
  } else if (dialogueIdx >= 0) {
    dialoguePart = text.substring(dialogueIdx + 4);
  } else if (summaryIdx >= 0) {
    summary = text.substring(summaryIdx + 4).trim();
    dialoguePart = "";
  }

  // 解析对话行：[M+:SS] 说话人：内容（分钟数可能超过两位，如 [120:30]）
  const lines: DialogueLine[] = [];
  const regex = /^\[(\d+):(\d{2})\]\s*(.+?)[：:](.+)$/;
  for (const rawLine of dialoguePart.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    const m = line.match(regex);
    if (m) {
      lines.push({
        time: `${m[1]}:${m[2]}`,
        sec: parseInt(m[1], 10) * 60 + parseInt(m[2], 10),
        speaker: m[3],
        content: m[4],
      });
    }
  }

  return { summary, lines };
}

/** 说话人标签颜色：销售蓝色，客户绿色，其它（无法区分角色）灰色 */
function speakerColor(speaker: string) {
  if (speaker === "销售") return "blue";
  if (speaker === "客户") return "green";
  return "grey";
}

const RATE_OPTIONS = [1, 1.5, 2];

/**
 * 通用录音播放弹窗组件
 * - 普通格式 (mp3/wav/m4a) 使用原生 <audio> 播放（支持倍速）
 * - AMR/AMR-WB (amr/awb) 使用 amr-audio-recorder 解码播放（不支持倍速）
 * - 传入 transcript 时，播放器与对话气泡联动：点击气泡跳转播放、播放中当前句高亮滚动
 */
export default function AudioPlayerModal({
  visible,
  url,
  title,
  fileName,
  transcript,
  onClose,
}: AudioPlayerModalProps) {
  const isAmr = useMemo(() => isAmrFormat(url, fileName || ""), [url, fileName]);
  const { summary, lines } = useMemo(() => parseTranscript(transcript || ""), [transcript]);
  const hasTranscriptView = lines.length > 0 || !!summary;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const amrRef = useRef<BenzAMRRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lineRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);

  /** 清除 AMR 位置定时器 */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /** 停止播放并清理 */
  const stopAndClean = useCallback(() => {
    clearTimer();
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (amrRef.current) {
      amrRef.current.stop();
      amrRef.current = null;
    }
    setPlaying(false);
    setPaused(false);
    setPosition(0);
    setDuration(0);
    setError("");
  }, [clearTimer]);

  /** AMR 初始化解码 */
  useEffect(() => {
    if (!visible || !isAmr || !url) return;

    setLoading(true);
    setError("");
    setPlaying(false);
    setPaused(false);
    setPosition(0);

    const amr = new BenzAMRRecorder();
    amrRef.current = amr;
    const audioType = isAmrWbFormat(url) || isAmrWbFormat(fileName || "") ? "audio/amr-wb" : undefined;
    (amr as any)
      .initWithUrl(url, audioType)
      .then(() => {
        setDuration(amr.getDuration());
        setLoading(false);
        // 自动播放
        amr.play();
      })
      .catch((e) => {
        console.error("AMR 解码失败:", e);
        setError("录音解码失败，请检查文件格式");
        setLoading(false);
      });

    amr.onPlay(() => {
      setPlaying(true);
      setPaused(false);
      clearTimer();
      timerRef.current = setInterval(() => {
        if (amrRef.current) {
          setPosition(amrRef.current.getCurrentPosition());
        }
      }, 200);
    });
    amr.onPause(() => {
      setPlaying(true);
      setPaused(true);
      clearTimer();
    });
    amr.onStop(() => {
      setPlaying(false);
      setPaused(false);
      clearTimer();
      setPosition(0);
    });
    amr.onEnded(() => {
      setPlaying(false);
      setPaused(false);
      clearTimer();
      setPosition(0);
    });

    return () => {
      clearTimer();
      if (amrRef.current) {
        amrRef.current.stop();
        amrRef.current = null;
      }
    };
  }, [visible, url, isAmr, fileName, clearTimer]);

  /** 原生 <audio> 事件绑定 */
  useEffect(() => {
    if (!visible || isAmr || !url) return;
    const audio = audioRef.current;
    if (!audio) return;

    setError("");
    setPlaying(false);
    setPaused(false);
    setPosition(0);
    setDuration(0);
    setRate(1);

    const onLoaded = () => setDuration(audio.duration || 0);
    const onTime = () => setPosition(audio.currentTime || 0);
    const onPlay = () => {
      setPlaying(true);
      setPaused(false);
    };
    const onPause = () => {
      setPlaying(true);
      setPaused(true);
    };
    const onEnded = () => {
      setPlaying(false);
      setPaused(false);
      setPosition(0);
    };
    const onError = () => setError("录音加载失败，可能是不支持的音频格式");

    audio.src = url;
    audio.load();
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.pause();
    };
  }, [visible, url, isAmr]);

  /** 播放/暂停切换 */
  const togglePlay = () => {
    if (isAmr) {
      amrRef.current?.pauseOrResume();
    } else {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) {
        audio.play().catch(() => setError("播放失败"));
      } else {
        audio.pause();
      }
    }
  };

  /** 跳转到指定秒。autoplay=true 时确保开始播放（用于点击对话气泡） */
  const seek = (sec: number, autoplay: boolean) => {
    if (isAmr) {
      amrRef.current?.play(sec);
      setPosition(sec);
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = sec;
    setPosition(sec);
    if (autoplay && audio.paused) {
      audio.play().catch(() => setError("播放失败"));
    }
  };

  /** 进度条拖动（保持当前播放/暂停状态） */
  const handleSeekBar = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seek(ratio * duration, false);
  };

  /** 切换倍速（仅原生格式） */
  const changeRate = (r: number) => {
    setRate(r);
    if (!isAmr && audioRef.current) {
      audioRef.current.playbackRate = r;
    }
  };

  /** 关闭弹窗 */
  const handleClose = useCallback(() => {
    stopAndClean();
    onClose();
  }, [stopAndClean, onClose]);

  /** 当前播放对应的对话行（取时间不晚于当前进度的最后一行） */
  const activeIndex = useMemo(() => {
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].sec <= position + 0.3) idx = i;
      else break;
    }
    return idx;
  }, [lines, position]);

  /** 播放中自动滚动到当前句 */
  useEffect(() => {
    if (activeIndex < 0 || !playing || paused) return;
    lineRefs.current[activeIndex]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex, playing, paused]);

  const progress = duration > 0 ? (position / duration) * 100 : 0;
  const formatLabel = isAmrWbFormat(url) || isAmrWbFormat(fileName || "") ? "AWB" : isAmr ? "AMR" : "音频";

  return (
    <Modal
      title={
        <span>
          <IconMusic style={{ marginRight: 6 }} />
          录音播放{title ? ` - ${title}` : ""}
        </span>
      }
      visible={visible}
      onCancel={handleClose}
      footer={null}
      width={hasTranscriptView ? 760 : 420}
      centered
    >
      <div style={{ padding: "4px 0 0" }}>
        {/* 隐藏的原生 <audio>：非 AMR 格式的播放引擎（AMR 模式下不使用） */}
        <audio ref={audioRef} style={{ display: "none" }} />

        {/* 加载中 */}
        {loading && (
          <div style={{ textAlign: "center", color: "var(--semi-color-text-2)", padding: 20 }}>
            正在解码录音...
          </div>
        )}

        {/* 错误 */}
        {error && (
          <div style={{ textAlign: "center", color: "var(--semi-color-danger)", padding: 20 }}>
            {error}
          </div>
        )}

        {/* 播放器 */}
        {!loading && !error && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Button
                theme="solid"
                type="primary"
                shape="circle"
                icon={playing && !paused ? <IconPause /> : <IconPlay />}
                onClick={togglePlay}
                size="small"
              />
              {/* 进度条 */}
              <div
                style={{
                  flex: 1,
                  height: 6,
                  background: "var(--semi-color-fill-0)",
                  borderRadius: 3,
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={handleSeekBar}
              >
                <div
                  style={{
                    width: `${progress}%`,
                    height: "100%",
                    background: "var(--semi-color-primary)",
                    borderRadius: 3,
                    transition: "width 0.2s linear",
                  }}
                />
              </div>
              {/* 时间 */}
              <span style={{ fontSize: 12, color: "var(--semi-color-text-2)", whiteSpace: "nowrap" }}>
                {formatTime(position)} / {formatTime(duration)}
              </span>
              {/* 倍速：仅原生格式支持（AMR 解码器不支持变速） */}
              {!isAmr && (
                <div style={{ display: "flex", gap: 2 }}>
                  {RATE_OPTIONS.map((r) => (
                    <Button
                      key={r}
                      size="small"
                      theme={rate === r ? "solid" : "light"}
                      type={rate === r ? "primary" : "tertiary"}
                      style={{ minWidth: 36, padding: "2px 0", fontSize: 12 }}
                      onClick={() => changeRate(r)}
                    >
                      {r}x
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* 格式信息 */}
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: "var(--semi-color-text-2)",
                textAlign: "center",
              }}
            >
              格式: {formatLabel}
              {isAmr ? "（暂不支持倍速）" : ""}
              {fileName ? ` · ${fileName}` : ""}
            </div>
          </>
        )}

        {/* ===== 转写联动视图 ===== */}
        {hasTranscriptView && (
          <div style={{ marginTop: 12, borderTop: "1px solid var(--semi-color-border)" , paddingTop: 12 }}>
            {/* 摘要卡片 */}
            {summary && (
              <div
                style={{
                  background: "#f0f7ff",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 12,
                  borderLeft: "4px solid #3b82f6",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6, color: "#1e40af" }}>
                  📋 智能摘要
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.7, color: "#334155", whiteSpace: "pre-wrap" }}>
                  {summary}
                </div>
              </div>
            )}

            {/* 对话列表：点击气泡跳转播放，当前句高亮 */}
            {lines.length > 0 && (
              <div style={{ maxHeight: 380, overflowY: "auto", paddingRight: 4 }}>
                {lines.map((line, i) => {
                  const isAgent = line.speaker === "销售";
                  const isActive = i === activeIndex;
                  return (
                    <div
                      key={i}
                      ref={(el) => { lineRefs.current[i] = el; }}
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 8,
                        flexDirection: isAgent ? "row" : "row-reverse",
                        cursor: "pointer",
                        borderRadius: 8,
                        padding: 4,
                        background: isActive ? "var(--semi-color-primary-light-default)" : "transparent",
                        transition: "background 0.2s",
                      }}
                      onClick={() => seek(line.sec, true)}
                      title="点击跳转到此句播放"
                    >
                      {/* 说话人标签 */}
                      <div style={{ flexShrink: 0, width: 56, textAlign: isAgent ? "right" : "left" }}>
                        <Tag color={speakerColor(line.speaker)} size="small" shape="circle">
                          {line.speaker}
                        </Tag>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{line.time}</div>
                      </div>
                      {/* 气泡 */}
                      <div
                        style={{
                          maxWidth: "75%",
                          padding: "8px 12px",
                          borderRadius: 12,
                          background: isAgent ? "#eff6ff" : "#f0fdf4",
                          border: isActive
                            ? isAgent ? "2px solid #3b82f6" : "2px solid #22c55e"
                            : isAgent ? "1px solid #bfdbfe" : "1px solid #bbf7d0",
                          fontSize: 14,
                          lineHeight: 1.7,
                          color: "#1e293b",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {line.content}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 无结构化内容时降级显示原文 */}
            {lines.length === 0 && !summary && (
              <div style={{ maxHeight: 380, overflowY: "auto", fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap", color: "#475569" }}>
                {transcript || "暂无转写内容"}
              </div>
            )}
          </div>
        )}

        {/* 不展示原始 URL，避免泄露签名地址；文件名已在格式信息行展示 */}
      </div>
    </Modal>
  );
}
